// src/pages/Skills.js
import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Spinner } from "react-bootstrap";
import {
  createSkill,
  getSkills,
  deleteSkill,
  updateSkill,
} from "../services/skillService";
import Loader from "../components/Loader";
import { PencilIcon, TrashIcon } from "lucide-react";
import { toast } from "react-toastify";

export default function Skills() {
  const [skills, setSkills] = useState([]);
  const [show, setShow] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [editingSkillId, setEditingSkillId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    setLoading(true);
    const skillsData = await getSkills();
    setSkills(skillsData);
    setLoading(false);
  };

  const handleAddSkill = async () => {
    if (!newSkill) return;
    setBtnLoading(true);
    const skillId = await createSkill({ name: newSkill });
    setSkills([...skills, { id: skillId, name: newSkill }]);
    setNewSkill("");
    setShow(false);
    setBtnLoading(false);
    toast.success("Skill added successfully");
  };

  const handleDeleteSkill = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    setLoading(true);
    await deleteSkill(id);
    setSkills(skills.filter((skill) => skill.id !== id));
    setLoading(false);
    toast.success("Skill deleted successfully");
  };

  const handleUpdateSkill = async () => {
    if (!editingSkillId) return;

    setBtnLoading(true);
    await updateSkill(editingSkillId, { name: newSkill });
    setSkills(
      skills.map((skill) =>
        skill.id === editingSkillId ? { ...skill, name: newSkill } : skill
      )
    );
    setBtnLoading(false);
    setEditingSkillId(null);
    setNewSkill("");
    setShow(false);
    toast.success("Skill updated successfully");
  };

  const handleEditSkill = (id) => {
    const skill = skills.find((s) => s.id === id);
    if (skill) {
      setEditingSkillId(id);
      setNewSkill(skill.name);
      setShow(true);
    }
  };

  const filteredSkills = skills
    .filter((s) =>
      Object.values(s).some((value) =>
        String(value).toLowerCase().includes(filter.toLowerCase())
      )
    )
    .sort((a, b) => {
      const aValue = String(a[sortKey]).toLowerCase();
      const bValue = String(b[sortKey]).toLowerCase();
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredSkills.length / pageSize);
  const paginatedSkills = filteredSkills.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Skills ({skills.length})</h2>
        <Button onClick={() => setShow(true)}>+ Add Skill</Button>
      </div>

      {loading && <Loader />}

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Skill Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedSkills.length === 0 && (
            <tr>
              <td colSpan="3" className="text-center">
                No skills found.
              </td>
            </tr>
          )}
          {paginatedSkills.map((s, idx) => (
            <tr key={s.id}>
              <td>{idx + 1}</td>
              <td>{s.name}</td>
              <td>
                <div className="flex space-x-2">
                  <Button variant="info" onClick={() => handleEditSkill(s.id)}>
                    <PencilIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteSkill(s.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Add Skill Modal */}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Skill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Skill Name</Form.Label>
            <Form.Control
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              placeholder="Enter skill name"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={btnLoading}
            onClick={editingSkillId ? handleUpdateSkill : handleAddSkill}
          >
            {btnLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="text-white"
                />
                <span className="ml-2">
                  {editingSkillId ? "Updating..." : "Saving..."}
                </span>
              </>
            ) : (
              <>{editingSkillId ? "Update Skill" : "Add Skill"}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
